import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  InputBase,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import ChatIcon from '@mui/icons-material/Chat';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CountrySelector } from './CountrySelector';
import { CATEGORY_CONFIG } from '../../features/home/categoryConfig';
import { CWY_COLORS } from '../../theme/colors';
import cwLogo from '../../assets/cw-red-logo.svg';
import cwFavicon from '../../assets/cw-favicon.svg';

interface PublicHeaderProps {
  onSearch?: (query: string) => void;
  searchValue?: string;
  showSearch?: boolean;
}

export const PublicHeader: React.FC<PublicHeaderProps> = ({
  onSearch,
  searchValue = '',
  showSearch = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, client, logout } = useAuth();
  const [search, setSearch] = useState(searchValue);
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [servicesAnchor, setServicesAnchor] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = search.trim();
    onSearch?.(q);
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/');
  };

  const appointmentsPath = '/appointments';

  const handleLogout = async () => {
    setAccountAnchor(null);
    await logout();
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' && !location.search;
    return location.pathname.startsWith(path.split('?')[0]);
  };

  const navButtonSx = (active: boolean) => ({
    color: '#fff',
    textTransform: 'none' as const,
    fontSize: '0.85rem',
    minWidth: 'auto',
    px: 1.5,
    whiteSpace: 'nowrap' as const,
    fontWeight: active ? 700 : 400,
    borderBottom: active ? `2px solid ${CWY_COLORS.amber}` : '2px solid transparent',
    borderRadius: 0,
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderBottomColor: CWY_COLORS.amber },
  });

  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* ── Top bar ── */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: CWY_COLORS.darkNavy }}>
        <Toolbar
          sx={{
            gap: { xs: 1, md: 2 },
            py: 0.5,
            minHeight: { xs: 56, md: 64 },
            width: '100%',
            px: { xs: 2, md: 4, lg: 5 },
            justifyContent: 'space-between',
          }}
        >
          {/* Mobile hamburger */}
          <IconButton
            edge="start"
            color="inherit"
            sx={{ display: { md: 'none' } }}
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
          >
            <Box
              component="img"
              src={cwFavicon}
              alt="CWY"
              sx={{ width: 32, height: 32, display: { xs: 'block', sm: 'none' } }}
            />
            <Box
              component="img"
              src={cwLogo}
              alt="Canadawale Yar"
              sx={{ height: 36, display: { xs: 'none', sm: 'block' } }}
            />
          </Box>

          {/* Search */}
          {showSearch && (
            <Box
              component="form"
              onSubmit={handleSearch}
              sx={{ flex: 1, display: 'flex', maxWidth: { xs: '100%', md: 720 }, mx: { xs: 0, md: 2 } }}
            >
              <InputBase
                placeholder="Search products and services..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  bgcolor: '#fff',
                  px: 2,
                  py: 0.75,
                  borderRadius: '4px 0 0 4px',
                  fontSize: '0.9rem',
                }}
              />
              <IconButton
                type="submit"
                sx={{
                  bgcolor: CWY_COLORS.amber,
                  borderRadius: '0 4px 4px 0',
                  px: 2,
                  '&:hover': { bgcolor: '#f3a847' },
                }}
              >
                <SearchIcon sx={{ color: CWY_COLORS.darkNavy }} />
              </IconButton>
            </Box>
          )}

          {/* Right side: country + account */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <CountrySelector />
            </Box>

            {isAuthenticated ? (
              <>
                <Button
                  color="inherit"
                  startIcon={<ChatIcon />}
                  onClick={() => navigate('/chat')}
                  sx={{ textTransform: 'none', display: { xs: 'none', sm: 'flex' } }}
                >
                  Chat
                </Button>
                <IconButton color="inherit" onClick={() => navigate('/chat')} sx={{ display: { sm: 'none' } }}>
                  <ChatIcon />
                </IconButton>
                <Button
                  color="inherit"
                  onClick={(e) => setAccountAnchor(e.currentTarget)}
                  sx={{ textTransform: 'none', gap: 1 }}
                >
                  <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, opacity: 0.8 }}>
                      Hello, {client?.name?.split(' ')[0]}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Account
                    </Typography>
                  </Box>
                  <Avatar
                    src={(client as { profile_picture?: string })?.profile_picture}
                    sx={{ width: 32, height: 32, bgcolor: CWY_COLORS.navyLight }}
                  >
                    {client?.name?.charAt(0)}
                  </Avatar>
                </Button>
                <Menu
                  anchorEl={accountAnchor}
                  open={Boolean(accountAnchor)}
                  onClose={() => setAccountAnchor(null)}
                  PaperProps={{ sx: { borderRadius: '10px', mt: 0.5 } }}
                >
                  <MenuItem onClick={() => { setAccountAnchor(null); navigate('/dashboard'); }}>My Account</MenuItem>
                  <MenuItem onClick={() => { setAccountAnchor(null); navigate('/membership'); }}>Membership</MenuItem>
                  <MenuItem onClick={() => { setAccountAnchor(null); navigate('/profile/wallet'); }}>My Crypto Wallet</MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout} sx={{ color: CWY_COLORS.priceRed }}>Sign out</MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="inherit"
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`)}
                sx={{ textTransform: 'none' }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, opacity: 0.8 }}>
                    Hello, sign in
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Account
                  </Typography>
                </Box>
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Secondary nav ── */}
      <Box
        sx={{
          bgcolor: CWY_COLORS.navy,
          color: '#fff',
          width: '100%',
          px: { xs: 2, md: 4, lg: 5 },
          py: 0.5,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 0,
        }}
      >
        <Button onClick={() => navigate('/')} startIcon={<HomeOutlinedIcon sx={{ fontSize: 17 }} />} sx={navButtonSx(isActive('/') && !location.search)}>
          Home
        </Button>

        {/* Services dropdown */}
        <Button
          onClick={(e) => setServicesAnchor(e.currentTarget)}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
          startIcon={<CategoryOutlinedIcon sx={{ fontSize: 17 }} />}
          sx={navButtonSx(false)}
        >
          Services
        </Button>
        <Menu
          anchorEl={servicesAnchor}
          open={Boolean(servicesAnchor)}
          onClose={() => setServicesAnchor(null)}
          PaperProps={{
            sx: {
              mt: 0.5,
              borderRadius: '10px',
              minWidth: 240,
              border: `1px solid ${CWY_COLORS.border}`,
            },
          }}
        >
          {CATEGORY_CONFIG.map((cat) => (
            <MenuItem
              key={cat.id}
              onClick={() => { setServicesAnchor(null); navigate(`/?section=categories`); }}
              sx={{ gap: 1.5, py: 1.25 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: cat.tileColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CategoryOutlinedIcon sx={{ fontSize: 16, color: cat.iconColor }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {cat.label}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        <Button
          onClick={() => navigate('/?section=products')}
          startIcon={<ShoppingBagOutlinedIcon sx={{ fontSize: 17 }} />}
          sx={navButtonSx(false)}
        >
          CWY Store
        </Button>

        <Button
          onClick={() => navigate(appointmentsPath)}
          startIcon={<EventAvailableIcon sx={{ fontSize: 17 }} />}
          sx={navButtonSx(isActive('/appointments'))}
        >
          Appointments
        </Button>

        <Button
          onClick={() => navigate('/membership')}
          startIcon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 17 }} />}
          sx={navButtonSx(isActive('/membership'))}
        >
          Membership
        </Button>

        {!isAuthenticated && (
          <Button
            onClick={() => navigate(`/login?redirect=${encodeURIComponent('/chat')}`)}
            startIcon={<ChatIcon sx={{ fontSize: 17 }} />}
            sx={{ ...navButtonSx(false), ml: 'auto', color: CWY_COLORS.amber }}
          >
            Chat with us
          </Button>
        )}
      </Box>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ bgcolor: CWY_COLORS.darkNavy, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="img" src={cwLogo} alt="CWY" sx={{ height: 30 }} />
        </Box>
        <Divider />
        <List sx={{ pt: 1 }}>
          <ListItemButton onClick={() => { navigate('/'); setMobileOpen(false); }}>
            <ListItemIcon><HomeOutlinedIcon /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>

          <ListItemButton onClick={() => setServicesExpanded(!servicesExpanded)}>
            <ListItemIcon><CategoryOutlinedIcon /></ListItemIcon>
            <ListItemText primary="Services" />
            {servicesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
          <Collapse in={servicesExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {CATEGORY_CONFIG.map((cat) => (
                <ListItemButton
                  key={cat.id}
                  sx={{ pl: 4 }}
                  onClick={() => { navigate('/?section=categories'); setMobileOpen(false); }}
                >
                  <ListItemText
                    primary={cat.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>

          <ListItemButton onClick={() => { navigate('/?section=products'); setMobileOpen(false); }}>
            <ListItemIcon><ShoppingBagOutlinedIcon /></ListItemIcon>
            <ListItemText primary="CWY Store" />
          </ListItemButton>

          <ListItemButton onClick={() => { navigate(appointmentsPath); setMobileOpen(false); }}>
            <ListItemIcon><EventAvailableIcon /></ListItemIcon>
            <ListItemText primary="Appointments" />
          </ListItemButton>

          <ListItemButton onClick={() => { navigate('/membership'); setMobileOpen(false); }}>
            <ListItemIcon><WorkspacePremiumOutlinedIcon /></ListItemIcon>
            <ListItemText primary="Membership" />
          </ListItemButton>

          <ListItemButton onClick={() => { navigate('/chat'); setMobileOpen(false); }}>
            <ListItemIcon><ChatIcon /></ListItemIcon>
            <ListItemText primary="Chat with us" />
          </ListItemButton>
        </List>

        <Divider />
        <Box sx={{ p: 2 }}>
          <CountrySelector />
        </Box>
      </Drawer>
    </Box>
  );
};
